import { AlertTriangle, RefreshCw, Wifi, WifiOff } from "lucide-react";
import { useTranslation } from "react-i18next";

import { useSyncStatus } from "../../offline/useSyncStatus";

/** Always-visible header status so the operator KNOWS their state: online vs
 *  offline, how many sales are still waiting to sync, and — separately, in red —
 *  any that the server rejected and need attention (never silent dead money). */
export function SyncStatus() {
  const { t } = useTranslation();
  const { online, pending, syncing, conflicts } = useSyncStatus();

  const attention = !online || pending > 0;
  const cls = attention
    ? "border-amber-300 bg-amber-50 text-amber-800"
    : "border-emerald-300 bg-emerald-50 text-emerald-800";

  let label: string;
  if (syncing) {
    label = t("sync.syncing");
  } else if (pending > 0) {
    label = t("sync.pending", { count: pending });
  } else if (online) {
    label = t("sync.online");
  } else {
    label = t("sync.offline");
  }

  return (
    <div className="inline-flex items-center gap-2">
      <div
        role="status"
        aria-live="polite"
        title={online ? t("sync.online") : t("sync.offline")}
        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-medium text-[12px] ${cls}`}
      >
        {syncing ? (
          <RefreshCw className="h-3.5 w-3.5 animate-spin" aria-hidden />
        ) : online ? (
          <Wifi className="h-3.5 w-3.5" aria-hidden />
        ) : (
          <WifiOff className="h-3.5 w-3.5" aria-hidden />
        )}
        <span>{label}</span>
      </div>
      {conflicts > 0 ? (
        <div
          role="status"
          aria-live="polite"
          className="inline-flex items-center gap-1.5 rounded-full border border-red-300 bg-red-50 px-2.5 py-1 font-medium text-[12px] text-red-800"
        >
          <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
          <span>{t("sync.attention", { count: conflicts })}</span>
        </div>
      ) : null}
    </div>
  );
}
